import moment from 'moment';
import { dayTypes } from '../../../../../helper';

export const WEEK_DAY_CODES = ['M', 'T', 'W', 'TH', 'F', 'S', 'SU'];

const AVAILABILITY_BASE_MONDAY = '2000-01-03T00:00:00';
const WEEK_MINUTES = 7 * 24 * 60;
const WEEK_UNBOUNDED_THRESHOLD = WEEK_MINUTES - 1;
const TWO_WEEK_UNBOUNDED_THRESHOLD = 2 * WEEK_MINUTES - 1;
export const LONGEST_AVAILABILITY_UNBOUNDED_MINS = 9999999;

const WEEKDAY_CODES = ['M', 'T', 'W', 'TH', 'F'];
const WEEKEND_CODES = ['S', 'SU'];


export function selectedDaysToWeekChecks(selectedDays) {
  const none = WEEK_DAY_CODES.reduce((acc, code) => ({ ...acc, [code]: false }), {});

  if (selectedDays == null) {
    return none;
  }

  if (Array.isArray(selectedDays)) {
    const next = { ...none };
    selectedDays.forEach((item) => {
      if (item?.value != null && item.value !== dayTypes.SELECT_DAYS) {
        next[item.value] = true;
      }
    });
    return next;
  }

  const preset = selectedDays.value;
  if (preset === dayTypes.ALL_DAYS) {
    return WEEK_DAY_CODES.reduce((acc, code) => ({ ...acc, [code]: true }), {});
  }
  if (preset === dayTypes.WEEKDAYS) {
    const next = { ...none };
    WEEKDAY_CODES.forEach((code) => {
      next[code] = true;
    });
    return next;
  }
  if (preset === dayTypes.WEEKENDS) {
    const next = { ...none };
    WEEKEND_CODES.forEach((code) => {
      next[code] = true;
    });
    return next;
  }
  if (preset === dayTypes.SELECT_DAYS) {
    return none;
  }

  return none;
}

export function selectedDaysToCodes(selectedDays) {
  const checks = selectedDaysToWeekChecks(selectedDays);
  return WEEK_DAY_CODES.filter((code) => checks[code]);
}

export function parseScheduleHour(value) {
  if (value == null || value === '' || value === -1) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    if (trimmed.includes(':')) {
      const parts = trimmed.split(':');
      const h = parseInt(parts[0], 10);
      const mRaw = parts[1] != null ? parseInt(parts[1], 10) : 0;
      if (Number.isNaN(h) || Number.isNaN(mRaw)) return null;
      if (h === 24 && mRaw === 0) return 24;
      if (h > 24 || (h === 24 && mRaw !== 0)) return null;
      if (mRaw < 0 || mRaw >= 60) return null;
      return h + mRaw / 60;
    }
    const n = parseFloat(trimmed);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function isOnHourGrid(x) {
  return x != null && Math.abs(x - Math.round(x)) < 1e-5;
}

function isOnHalfHourGrid(x) {
  return x != null && Math.abs(x * 2 - Math.round(x * 2)) < 1e-5;
}

export function buildScheduleStartValues(slotMinutes) {
  const step = slotMinutes === 60 ? 60 : 30;
  const out = [];
  for (let min = 0; min < 24 * 60; min += step) {
    out.push(min / 60);
  }
  return out;
}

export function getScheduleEndOptionValues(startFractionalHour, slotMinutes) {
  const s = Number(startFractionalHour);
  if (!Number.isFinite(s)) return [];
  const step = slotMinutes === 60 ? 60 : 30;
  const out = [];
  for (let min = Math.round(s * 60) + step; min < 24 * 60; min += step) {
    out.push(min / 60);
  }
  out.push(24);
  return out;
}

export function formatScheduleFractionalHour12h(fractionalHour) {
  if (fractionalHour == null || Number.isNaN(Number(fractionalHour))) {
    return '—';
  }
  const fh = Number(fractionalHour);
  if (Math.abs(fh - 24) < 1e-6) {
    return '12:00 am';
  }
  const totalMinutes = Math.round(fh * 60);
  const capped = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  return moment().startOf('day').add(capped, 'minutes').format('h:mm a');
}

/**
 * When slot interval changes, drop or adjust start/end so they stay on the new grid.
 * @returns {{ next: Record<string, { start, end }>, changed: boolean }}
 */
export function snapDailyScheduleToNewSlotGrid(dailySchedule, nextSlotMinutes) {
  const next = { ...dailySchedule };
  let changed = false;
  for (const code of WEEK_DAY_CODES) {
    const row = next[code];
    if (!row) continue;
    const s = parseScheduleHour(row.start);
    const e = parseScheduleHour(row.end);
    if (s == null && e == null) continue;
    let ns = s;
    let ne = e;
    if (nextSlotMinutes === 60) {
      if (s != null && !isOnHourGrid(s)) {
        ns = null;
        ne = null;
      } else if (e != null && Math.abs(e - 24) >= 1e-5 && !isOnHourGrid(e)) {
        ne = null;
      }
    } else {
      if (s != null && !isOnHalfHourGrid(s)) {
        ns = Math.round(s * 2) / 2;
      }
      if (e != null && Math.abs(e - 24) >= 1e-5 && !isOnHalfHourGrid(e)) {
        ne = Math.round(e * 2) / 2;
      }
    }
    if (ns != null && ne != null && ne <= ns) {
      ne = null;
    }
    if (ns !== row.start || ne !== row.end) {
      next[code] = { ...row, start: ns, end: ne };
      changed = true;
    }
  }
  return { next, changed };
}

export function buildAmenitySchedulesFromDailyForm(selectedDays, dailySchedule) {
  const checks = selectedDaysToWeekChecks(selectedDays);
  return WEEK_DAY_CODES.map((code, index) => {
    const day_of_week = index + 1;
    const row = dailySchedule?.[code];
    const start = parseScheduleHour(row?.start);
    const end = parseScheduleHour(row?.end);
    const is_closed =
      !checks[code] || start == null || end == null || end === start;
    return { day_of_week, start, end, is_closed };
  });
}

function buildScheduleIntervals(schedules) {
  const base = moment(AVAILABILITY_BASE_MONDAY);
  const intervals = [];

  for (const s of schedules) {
    if (s?.is_closed) continue;
    const startH = s.start;
    const endH = s.end;
    if (startH == null || endH == null) continue;
    if (typeof startH !== 'number' || typeof endH !== 'number') continue;
    const dow = s.day_of_week;
    if (dow == null || dow < 1 || dow > 7) continue;

    const dayBase = base.clone().add(dow - 1, 'days').startOf('day');
    const start = dayBase.clone().add(startH, 'hours');
    let end;
    if (Math.abs(endH - 24) < 1e-6) {
      end = dayBase.clone().add(1, 'day');
    } else {
      end = dayBase.clone().add(endH, 'hours');
    }
    if (!end.isAfter(start)) {
      end.add(1, 'day');
    }
    intervals.push({ start, end });
  }
  return intervals;
}

/**
 * Longest continuous open window.`:
 * multi-day with ≥2 open days appends a +7 copy so Sun→Mon wraps merge, then take max.
 *
 * @param {Array<{ day_of_week: number, start: number|null, end: number|null, is_closed: boolean }>} schedules — `day_of_week` 1 = Monday … 7 = Sunday
 * @param {boolean} allowMultiDay
 * @returns {number} minutes, or {@link LONGEST_AVAILABILITY_UNBOUNDED_MINS} for full-week ring streaks
 */
export function getLongestContinuousAvailabilityMins(schedules, allowMultiDay) {
  const intervals = buildScheduleIntervals(schedules);
  if (intervals.length === 0) {
    return 0;
  }

  let working = intervals.map(({ start, end }) => ({
    start: start.clone(),
    end: end.clone()
  }));

  // Week duplication only when multi-day bookings are allowed — otherwise only
  // same-calendar-day windows apply. Need ≥2 open days for a wrap to exist.
  const weekDuplicated = !!allowMultiDay && working.length >= 2;
  if (weekDuplicated) {
    const shiftedFwd = working.map(({ start, end }) => ({
      start: start.clone().add(7, 'days'),
      end: end.clone().add(7, 'days')
    }));
    working = working.concat(shiftedFwd);
  }

  working.sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const merged = [];
  for (const current of working) {
    if (merged.length === 0) {
      merged.push({ start: current.start.clone(), end: current.end.clone() });
      continue;
    }
    const last = merged[merged.length - 1];
    if (current.start.valueOf() <= last.end.valueOf()) {
      if (current.end.valueOf() > last.end.valueOf()) {
        last.end = current.end.clone();
      }
    } else {
      merged.push({ start: current.start.clone(), end: current.end.clone() });
    }
  }

  let longestMins = 0;
  for (const { start, end } of merged) {
    const mins = end.diff(start, 'minutes');
    if (mins > longestMins) longestMins = mins;
  }

  const unboundedThreshold = weekDuplicated
    ? TWO_WEEK_UNBOUNDED_THRESHOLD
    : WEEK_UNBOUNDED_THRESHOLD;
  if (longestMins >= unboundedThreshold) {
    return LONGEST_AVAILABILITY_UNBOUNDED_MINS;
  }
  return longestMins;
}

/** Post-booking auto-block: slot multiples, capped at 6 hours */
export const AUTO_BLOCK_MAX_MINUTES = 360;
/** Max days ahead residents can book an amenity. */
export const MAX_ADVANCE_BOOKING_DAYS = 90;
const BOOKING_LIMIT_UNIT_DAYS = 1;

/** Form/API edge only. Auto-block helpers assume slot is already 30|60. */
export function normalizeSlotDurationMinutes(slotMinutes) {
  return Number(slotMinutes) === 60 ? 60 : 30;
}

function alignedAutoBlockCapMinutes(slot, maxMinutes = AUTO_BLOCK_MAX_MINUTES) {
  const cap = Number.isFinite(Number(maxMinutes)) ? Number(maxMinutes) : AUTO_BLOCK_MAX_MINUTES;
  const limited = Math.min(AUTO_BLOCK_MAX_MINUTES, cap);
  if (limited < slot) return 0;
  return Math.floor(limited / slot) * slot;
}

export function isValidAutoBlockDuration(
  durationMinutes,
  slotMinutes,
  maxMinutes = AUTO_BLOCK_MAX_MINUTES
) {
  const n = Number(durationMinutes);
  const max = alignedAutoBlockCapMinutes(slotMinutes, maxMinutes);
  return (
    Number.isFinite(n) && n > 0 && max >= slotMinutes && n <= max && n % slotMinutes === 0
  );
}

export function snapAutoBlockDuration(
  durationMinutes,
  slotMinutes,
  maxMinutes = AUTO_BLOCK_MAX_MINUTES
) {
  const max = alignedAutoBlockCapMinutes(slotMinutes, maxMinutes);
  const n = Number(durationMinutes);
  if (max < slotMinutes) return max > 0 ? max : slotMinutes;
  if (!Number.isFinite(n) || n <= 0) return slotMinutes;
  if (isValidAutoBlockDuration(n, slotMinutes, max)) return n;
  const floored = Math.floor(n / slotMinutes) * slotMinutes;
  if (floored < slotMinutes) return slotMinutes;
  if (floored > max) return max;
  return floored;
}

function requiredBookingMinutes({
  bookingLimitsUnit,
  allowMultiDay,
  slotMinutes,
  minDurationValue,
  minReservationHours
}) {
  let requiredBooking = slotMinutes;

  if (allowMultiDay) {
    const min = Number(minDurationValue);
    if (Number.isFinite(min) && min > 0) {
      const minMins =
        Number(bookingLimitsUnit) === BOOKING_LIMIT_UNIT_DAYS ? min * 24 * 60 : min * 60;
      if (minMins > requiredBooking) requiredBooking = minMins;
    }
    return requiredBooking;
  }

  const minH = Number(minReservationHours);
  if (Number.isFinite(minH) && minH > 0) {
    const minMins = minH * 60;
    if (minMins > requiredBooking) requiredBooking = minMins;
  }
  return requiredBooking;
}

/**
 * Cap block durations so at least one continuous open interval can fit
 * max(min booking, slot) + block. Same longest-window bar as BE schedule validation.
 * Callers must pass a normalized slot (30|60).
 */
export function maxAllowedAutoBlockMinutes({
  bookingLimitsUnit,
  allowMultiDay,
  slotMinutes,
  selectedDays,
  dailySchedule,
  minDurationValue,
  minReservationHours
}) {
  const cap = alignedAutoBlockCapMinutes(slotMinutes, AUTO_BLOCK_MAX_MINUTES);
  const schedules = buildAmenitySchedulesFromDailyForm(selectedDays, dailySchedule);
  const longest = getLongestContinuousAvailabilityMins(schedules, !!allowMultiDay);
  if (longest <= 0 || longest >= LONGEST_AVAILABILITY_UNBOUNDED_MINS) return cap;

  const requiredBooking = requiredBookingMinutes({
    bookingLimitsUnit,
    allowMultiDay: !!allowMultiDay,
    slotMinutes,
    minDurationValue,
    minReservationHours
  });

  return alignedAutoBlockCapMinutes(slotMinutes, longest - requiredBooking);
}

export function formatAutoBlockDurationLabel(minutes, t) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 60) {
    return t('add_amenity_block_n_min', { defaultValue: '{{n}} min', n });
  }
  const hours = n / 60;
  if (Number.isInteger(hours)) {
    if (hours === 1) {
      return t('add_amenity_block_1_hr', { defaultValue: '1 hr' });
    }
    return t('add_amenity_block_n_hrs', { defaultValue: '{{n}} hrs', n: hours });
  }
  const h = Math.floor(n / 60);
  const m = n % 60;
  return t('add_amenity_block_h_m', { defaultValue: '{{h}} hr {{m}} min', h, m });
}

function formatAvailabilityAmount(n) {
  if (!Number.isFinite(n)) {
    return '';
  }
  const rounded = Math.round(n * 10) / 10;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return rounded.toFixed(1);
}

export function formatAvailabilityHoursFromMinutes(totalMins) {
  return formatAvailabilityAmount(totalMins / 60);
}

export function formatAvailabilityDaysFromMinutes(totalMins) {
  return formatAvailabilityAmount(totalMins / (24 * 60));
}

export function applyWeekSelectionFromChecks(
  checks,
  { setValue, dayOptions, t, shouldDirty = false }
) {
  const isOn = (code) => !!checks[code];
  const setValueOpts = shouldDirty ? { shouldDirty: true } : undefined;

  if (WEEK_DAY_CODES.every((c) => isOn(c))) {
    setValue('selectedDays', { value: dayTypes.ALL_DAYS, label: t('All days') }, setValueOpts);
    return;
  }

  const onlyWeekdays =
    WEEKDAY_CODES.every((c) => isOn(c)) && WEEKEND_CODES.every((c) => !isOn(c));
  if (onlyWeekdays) {
    setValue('selectedDays', { value: dayTypes.WEEKDAYS, label: t('Weekdays') }, setValueOpts);
    return;
  }

  const onlyWeekends =
    WEEKEND_CODES.every((c) => isOn(c)) && WEEKDAY_CODES.every((c) => !isOn(c));
  if (onlyWeekends) {
    setValue('selectedDays', { value: dayTypes.WEEKENDS, label: t('Weekends') }, setValueOpts);
    return;
  }

  const picked = WEEK_DAY_CODES.filter((c) => isOn(c)).map((code) => {
    const day = dayOptions.find((d) => d.value === code);
    return { value: code, label: day ? day.label : code };
  });

  setValue('selectedDays', picked, setValueOpts);
}
